import React from 'react';
import { Card, CardContent } from "../../../components/ui/card"; // Assuming these are available from shadcn/ui
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table"; // Assuming these are available from shadcn/ui

export function LeaderboardSkeleton() {
    const numberOfCards = 3;
    // Number of skeleton rows to display in the table
    const numberOfRows = 10;

    return (
        <div className="p-4 md:p-6 mb-24 animate-pulse">
            {/* Skeleton for Breadcrumb and Title */}
            <div className="flex flex-col items-start gap-2 mb-0 lg:mb-6">
                {/* Back button skeleton */}
                <div className="h-10 w-10 rounded-full bg-[var(--q3-surface-dim)] mb-2"></div>
                {/* Title skeleton */}
                <div className="h-8 w-64 bg-[var(--q3-surface-dim)] rounded-md"></div>
                {/* Breadcrumb skeleton */}
                <div className="h-5 w-96 bg-[var(--q3-surface-dim)] rounded-md mb-6"></div>
            </div>

            {/* Skeleton for Tabs (if present) */}
            <div className="mb-8 flex space-x-4">
                <div className="h-9 w-24 bg-[var(--q3-surface-dim)] rounded-full"></div>
                <div className="h-9 w-28 bg-[var(--q3-surface-dim)] rounded-full"></div>
            </div>

            {/* Skeleton for Student Cards */}
            <div className="hidden md:flex justify-between flex-wrap mb-8 gap-4">
                {Array.from({ length: numberOfCards }).map((_, index) => (
                    <Card key={index} className='rounded-3xl w-[264px] h-[320px] border-1 border-[var(--q3-stroke-normal)] bg-[var(--q3-surface-default)]'>
                        <CardContent className="px-4 py-2 flex flex-col justify-center items-center w-full h-full">
                            <div className="relative mb-4">
                                {/* Avatar skeleton */}
                                <div className="h-16 w-16 mx-auto mb-2 rounded-full bg-[var(--q3-surface-dimmer)]"></div>
                                {/* Rank display skeleton */}
                                <div className="absolute bottom-[-14px] left-1/2 transform -translate-x-1/2 h-8 w-12 bg-[var(--q3-surface-dim)] rounded-md"></div>
                            </div>
                            {/* Name skeleton */}
                            <div className="h-6 w-3/4 bg-[var(--q3-surface-dim)] rounded-md mb-2"></div>
                            {/* Rank badge skeleton */}
                            <div className="h-7 w-24 bg-[var(--q3-surface-dim)] rounded-full mb-4"></div>
                            <div className="space-y-3 w-full text-sm">
                                {/* Score line skeleton */}
                                <div className="flex items-center justify-between">
                                    <div className="h-4 w-20 bg-[var(--q3-surface-dim)] rounded-md"></div>
                                    <div className="h-4 w-16 bg-[var(--q3-surface-dim)] rounded-md"></div>
                                </div>
                                {/* Subject score lines skeleton */}
                                {Array.from({ length: 3 }).map((_, i) => (
                                    <div key={`subject-${i}`} className="flex items-center justify-between">
                                        <div className="h-4 w-20 bg-[var(--q3-surface-dim)] rounded-md"></div>
                                        <div className="h-4 w-16 bg-[var(--q3-surface-dim)] rounded-md"></div>
                                    </div>
                                ))}
                                {/* Accuracy line skeleton */}
                                <div className="flex items-center justify-between">
                                    <div className="h-4 w-20 bg-[var(--q3-surface-dim)] rounded-md"></div>
                                    <div className="h-4 w-16 bg-[var(--q3-surface-dim)] rounded-md"></div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Skeleton for Leaderboard Table */}
            <div className="rounded-lg border overflow-hidden border-[var(--q3-stroke-normal)] bg-[var(--q3-surface-default)]">
                <div className="overflow-x-auto">
                    <Table className='table-fixed'>
                        <TableHeader className='h-16'>
                            <TableRow style={{ backgroundColor: "var(--q3-surface-dim)" }}>
                                <TableHead className="w-[80px] font-semibold">
                                    <div className="h-4 w-12 bg-[var(--q3-surface-default)] rounded-md"></div>
                                </TableHead>
                                <TableHead className="w-[200px] font-semibold">
                                    <div className="h-4 w-24 bg-[var(--q3-surface-default)] rounded-md"></div>
                                </TableHead>
                                <TableHead className="w-[140px] font-semibold text-center">
                                    <div className="h-4 w-20 bg-[var(--q3-surface-default)] rounded-md mx-auto"></div>
                                </TableHead>
                                {/* Assuming 3 subject columns + Accuracy + Compare */}
                                {Array.from({ length: 3 }).map((_, index) => (
                                    <TableHead key={`header-sub-${index}`} className="w-[80px] font-semibold text-center">
                                        <div className="h-4 w-12 bg-[var(--q3-surface-default)] rounded-md mx-auto"></div>
                                    </TableHead>
                                ))}
                                <TableHead className="w-[100px] font-semibold text-center">
                                    <div className="h-4 w-16 bg-[var(--q3-surface-default)] rounded-md mx-auto"></div>
                                </TableHead>
                                <TableHead className="w-[120px] font-semibold text-center">
                                    <div className="h-4 w-16 bg-[var(--q3-surface-default)] rounded-md mx-auto"></div>
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {Array.from({ length: numberOfRows }).map((_, rowIndex) => (
                                <TableRow key={rowIndex} className='h-16'>
                                    <TableCell className="font-medium">
                                        <div className="h-7 w-7 rounded-full bg-[var(--q3-surface-dim)]"></div>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-3">
                                            <div className="h-8 w-8 rounded-full bg-[var(--q3-surface-dimmer)]"></div>
                                            <div className="h-5 w-32 bg-[var(--q3-surface-dim)] rounded-md"></div>
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-center">
                                        <div className="h-8 w-24 bg-[var(--q3-surface-dim)] rounded-full mx-auto"></div>
                                    </TableCell>
                                    {Array.from({ length: 3 }).map((_, colIndex) => (
                                        <TableCell key={`cell-sub-${rowIndex}-${colIndex}`} className="text-center font-medium">
                                            <div className="h-5 w-10 bg-[var(--q3-surface-dim)] rounded-md mx-auto"></div>
                                        </TableCell>
                                    ))}
                                    <TableCell className="text-center font-medium">
                                        <div className="h-5 w-12 bg-[var(--q3-surface-dim)] rounded-md mx-auto"></div>
                                    </TableCell>
                                    <TableCell className="text-center">
                                        <div className="h-8 w-20 bg-[var(--q3-surface-dim)] rounded-full mx-auto"></div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </div>

            {/* Skeleton for Pagination */}
            <div className="mt-6 flex justify-center">
                <div className="flex space-x-2">
                    <div className="h-10 w-10 rounded-md bg-[var(--q3-surface-dim)]"></div>
                    <div className="h-10 w-10 rounded-md bg-[var(--q3-surface-dim)]"></div>
                    <div className="h-10 w-10 rounded-md bg-[var(--q3-surface-dim)]"></div>
                    <div className="h-10 w-10 rounded-md bg-[var(--q3-surface-dim)]"></div>
                    <div className="h-10 w-10 rounded-md bg-[var(--q3-surface-dim)]"></div>
                </div>
            </div>
        </div>
    );
}
